import { lazy, Suspense, useId, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertTriangle,
  Ban,
  Download,
  Edit3,
  Eye,
  FileX,
  FileText,
  Link2,
  MoreVertical,
  Receipt,
  ReceiptText,
  Send,
  DollarSign,
  ArrowRightLeft,
  Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/atoms/button';
import { Textarea } from '@/components/atoms/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { OverflowMenuContent, RowOverflowMenuTrigger } from '@/components/molecules';
import { Input } from '@/components/atoms/input';
import { useApplyLateFee } from '@/hooks/invoices/useApplyLateFee';
import { useDeleteInvoice } from '@/hooks/invoices/useDeleteInvoice';
import { normalizeInvoice, useInvoice } from '@/hooks/invoices/useInvoice';
import { invoicePaymentsQueryOptions } from '@/hooks/invoices/useInvoicePayments';
import { DEFAULT_PAYMENT_LIST_FILTERS } from '@/components/organisms/payments/payment-list-filters';
import { DEFAULT_RECEIPT_LIST_FILTERS } from '@/components/organisms/receipts/receipt-list-filters';
import { receiptKeys } from '@/lib/queryKeys';
import type { ReceiptDetail } from '@/hooks/receipts/types';
import { useRemindInvoice } from '@/hooks/invoices/useRemindInvoice';
import { useSendInvoice } from '@/hooks/invoices/useSendInvoice';
import { useUpdateInvoice } from '@/hooks/invoices/useUpdateInvoice';
import { useVoidInvoice } from '@/hooks/invoices/useVoidInvoice';
import { useWriteOffInvoice } from '@/hooks/invoices/useWriteOffInvoice';
import { usePromotions } from '@/hooks/operations/usePromotions';
import { useContactSummary } from '@/hooks/contacts/useContactSummary';
import { ApplyCreditModal } from '@/components/organisms/invoices/ApplyCreditModal';
import { DEFAULT_CREDIT_MEMO_LIST_FILTERS } from '@/components/organisms/credit-memos/credit-memo-list-filters';
import { useInvoicePdfCompany } from '@/hooks/invoices/useInvoicePdfCompany';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { useHasCapability, useHasPermission, useIsFieldHidden } from '@/hooks/usePermissions';
import { invoiceCollectionBlockedReason } from '@/components/organisms/invoices/support/invoice-clearing';
import { shownInvoiceMoney } from '@/lib/invoices/shown-invoice-money';
import { apiGet, operationsClient } from '@/lib/api';
import { endpoints, invoiceEndpoints } from '@/lib/api/endpoints';
import type { EstimateDocLayout } from '@/types/estimateDocTemplate';
import { computeEffectiveInvoiceStatus } from '@/lib/invoice-status';
import { getContactDisplayName } from '@/lib/contacts';
import type { Invoice, Payment } from '@/types/invoice';
import type { Promotion } from '@/types/promotion';
import {
  PaymentLinkDialog,
  RecordPaymentDialog,
  SendDialog,
} from '@/components/organisms/invoices/InvoiceActions';

const InvoicePDFPreview = lazy(() => import('@/components/documents/invoices/InvoicePDFPreview'));

function invoiceReference(invoice: Pick<Invoice, 'invoiceNumber'>): string {
  return invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : 'unavailable invoice';
}

interface InvoiceActionsMenuProps {
  invoice: Invoice;
  onViewDetail?: () => void;
  onPayment?: () => void;
  onPaymentLink?: () => void;
  onPreviewPDF?: () => void;
  onDownloadPDF?: () => void;
  onDiscarded?: () => void;
  hideViewDetail?: boolean;
  /**
   * Leave out "Send reminder". The detail panel sets it when a refund reopened
   * the invoice: the balance is money the business gave back, not a payment
   * the customer is late with, so a reminder is the wrong message.
   */
  hideReminder?: boolean;
  /**
   * `menu` (default) renders the canonical ⋮ kebab. `header-row` renders the
   * contextual primary actions as inline buttons (for the detail Next-step
   * card) reusing the very same handlers/dialogs — no duplicated logic.
   */
  variant?: 'menu' | 'header-row';
}

export function InvoiceActionsMenu({
  invoice,
  onViewDetail,
  onPayment,
  onPaymentLink,
  onPreviewPDF,
  onDownloadPDF,
  onDiscarded,
  hideViewDetail = false,
  hideReminder = false,
  variant = 'menu',
}: InvoiceActionsMenuProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const canCollectPayment = useHasCapability('can_collect_payment');
  // The capability alone does not make the write succeed. `POST /v2/billing/payments`
  // demands `payments:create` AND the capability; apply-credit demands
  // `payments:edit`. A role holding the capability without the grant (crew_lead,
  // field_technician today) would otherwise open a dialog that submits into a 403.
  const canCreatePayments = useHasPermission('payments:create');
  const canEditPayments = useHasPermission('payments:edit');
  const canEditInvoices = useHasPermission('invoices:edit');
  const pricingHidden = useIsFieldHidden('PRICING');
  const paymentInfoHidden = useIsFieldHidden('PAYMENT_INFO');
  const customerFinancialsHidden = useIsFieldHidden('CUSTOMER_FINANCIALS');
  const paymentActionsHidden = pricingHidden || paymentInfoHidden || customerFinancialsHidden;
  const sendInvoice = useSendInvoice();
  const voidInvoice = useVoidInvoice();
  const writeOffInvoice = useWriteOffInvoice();
  const deleteInvoice = useDeleteInvoice();
  const remindInvoice = useRemindInvoice();
  const applyLateFee = useApplyLateFee();
  const updateInvoice = useUpdateInvoice();
  const companyTimeZone = useCompanyTimezone();

  const [showSend, setShowSend] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showApplyCredit, setShowApplyCredit] = useState(false);
  const [showPaymentLink, setShowPaymentLink] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showLateFee, setShowLateFee] = useState(false);
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [showWriteOffConfirm, setShowWriteOffConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [resolvingReceipt, setResolvingReceipt] = useState(false);

  const fullInvoiceQuery = useInvoice(invoice.id, { enabled: showPreview });
  const promotionsQuery = usePromotions({ status: 'active' }, { enabled: showPromotionDialog });
  const status = computeEffectiveInvoiceStatus(invoice) as string;
  const isDraft = status === 'draft';
  const isOpen = status === 'open' || status === 'sent' || status === 'viewed';
  const isPartial = status === 'partial';
  const isPaid = status === 'paid';
  const isOverdue = status === 'overdue';
  const isVoid = status === 'void' || status === 'written_off';
  const isRefunded = status === 'refunded';
  const balanceDue = invoice.balanceDue ?? 0;
  // The collection dialogs default and cap their amounts on what the invoice
  // SHOWS owing, so they never offer to collect money already clearing. Action
  // gating and the server's guards stay on the stored `balanceDue`. A viewer
  // the server withholds clearing from gets the stored figure back.
  const shownBalanceDue = shownInvoiceMoney(invoice).balance ?? balanceDue;
  // PAYMENT_INFO/PRICING make the server DELETE `balanceDue` rather than zero
  // it, so an absent field means "not allowed to see it", not "nothing
  // left to collect". Without this distinction a masked collector's stripped
  // balance reads as settled and every payment action disappears.
  const balanceDueKnown = invoice.financialFieldPresence?.balanceDue !== false;
  const hasCollectableBalance = balanceDueKnown ? balanceDue > 0 : true;
  const lateFeeApplied =
    Boolean((invoice as Invoice & { lateFeeApplied?: boolean }).lateFeeApplied) ||
    (invoice.lineItems ?? []).some((item) => {
      const record = item as unknown as Record<string, unknown>;
      return record['source'] === 'LATE_FEE' || record['code'] === 'LATE-FEE';
    });
  // Collecting is not the same as seeing. A technician takes the cheque at the
  // door while PAYMENT_INFO keeps the invoice's balance and payment history
  // hidden, so this gate asks for the capability and a
  // collectable invoice — NOT for financial visibility. The actions that do
  // expose money (reminder, credit memo, late fee, payment link) stay masked.
  const canRecordPayment =
    canCollectPayment &&
    canCreatePayments &&
    hasCollectableBalance &&
    (isOpen || isPartial || isOverdue);
  // Money already in flight that covers the balance — "$500.00 pending ACH",
  // or null. The control stays RENDERED and goes disabled: a button that
  // disappears mid-clearing reads as a broken page, and nothing else on this
  // menu says a debit is on its way. What re-enables it is the same server
  // field going away, which happens either way the debit resolves — settled,
  // and the invoice is paid; returned, and the balance is owed again — so
  // there is no local state to unwind and no limit on how often it may fire.
  const collectionBlockedReason = invoiceCollectionBlockedReason(invoice, formatCurrency);
  const recordPaymentBlocked = canRecordPayment && collectionBlockedReason !== null;
  const blockedReasonId = useId();
  // Payment credit is distinct from unapplied credit-memo balance. This action
  // consumes unapplied payments only; memo application has its own flow. Only
  // fetch when the invoice is collectable and has a contact.
  const contactId = invoice.contactId ?? '';
  const invoiceCollectable =
    !paymentActionsHidden && balanceDue > 0 && (isOpen || isPartial || isOverdue);
  const contactSummary = useContactSummary(invoiceCollectable && contactId ? contactId : '');
  const creditAvailable = Number(
    contactSummary.data?.paymentCreditAvailable ?? contactSummary.data?.creditAvailable ?? 0,
  );
  const canApplyCredit =
    canCollectPayment &&
    canEditPayments &&
    invoiceCollectable &&
    Boolean(contactId) &&
    creditAvailable > 0;
  const canSendReminder =
    !hideReminder && !paymentActionsHidden && balanceDue > 0 && (isOpen || isPartial || isOverdue);
  // The same money in flight: a reminder would ask the customer to pay what
  // their bank is already sending. The server refuses it too, and the
  // automatic reminders hold back for the same reason.
  const reminderBlocked = canSendReminder && collectionBlockedReason !== null;
  const reminderBlockedReasonId = useId();
  // An issued invoice can still be SENT — not just reminded about. The one
  // auto-issued on job completion is the case: with the tenant's auto-send off
  // it lands OPEN with `sentAt` set and the customer never received anything,
  // and a reminder is the wrong first contact. The server's send-email /
  // send-sms accept OPEN and PARTIALLY_PAID (overdue is OPEN past its due date).
  const canSendIssued = !pricingHidden && !isDraft && (isOpen || isPartial || isOverdue);
  const canApplyLateFee = !paymentActionsHidden && balanceDue > 0 && isOverdue && !lateFeeApplied;
  const canPaymentLink =
    !paymentActionsHidden && !isDraft && !isVoid && !isRefunded && balanceDue > 0;
  const canIssueCreditMemo =
    canEditInvoices &&
    !pricingHidden &&
    !customerFinancialsHidden &&
    Boolean(contactId) &&
    !isDraft &&
    !isVoid;
  const canApplyPromotion = !pricingHidden && (isDraft || isOpen || isOverdue);
  const canOpenPaymentReceipt = !pricingHidden && !paymentInfoHidden && isPaid;
  const canRenderPdf = !pricingHidden;
  const canVoid = !isDraft && !isPaid && !isVoid && !isRefunded;
  const canWriteOff = balanceDue > 0 && (isOpen || isPartial || isOverdue);
  const canDiscard = isDraft;
  const showViewDetail = !hideViewDetail;

  const isMutating =
    sendInvoice.isPending ||
    voidInvoice.isPending ||
    writeOffInvoice.isPending ||
    deleteInvoice.isPending ||
    remindInvoice.isPending ||
    applyLateFee.isPending ||
    updateInvoice.isPending ||
    downloading;

  const companyForPDF = useInvoicePdfCompany();

  function goToDetail() {
    if (onViewDetail) {
      onViewDetail();
      return;
    }

    void router.navigate({
      to: '/dashboard/invoices/$id',
      params: { id: invoice.id },
      search: (prev) => ({ ...prev, tab: 'overview' }) as never,
    });
  }

  const canEditInvoice =
    canEditInvoices &&
    !pricingHidden &&
    !paymentInfoHidden &&
    !customerFinancialsHidden &&
    (isDraft || isOpen || isOverdue);

  function goToEdit() {
    void router.navigate({
      to: '/dashboard/invoices/$id/edit',
      params: { id: invoice.id },
      search: (prev) => prev as never,
    });
  }

  async function goToPaymentReceipt() {
    // invoice.payments can be empty on the base object, so resolve the payment
    // on demand from the same cached source used by the Payments tab.
    const pickId = (list: Payment[] | undefined) =>
      list?.find((p) => p.kind === 'payment' && p.paymentId)?.paymentId ??
      list?.find((p) => p.paymentId)?.paymentId ??
      list?.[0]?.id;

    let paymentId = pickId(invoice.payments);

    setResolvingReceipt(true);
    try {
      if (!paymentId) {
        try {
          const payments = await queryClient.fetchQuery(invoicePaymentsQueryOptions(invoice.id));
          paymentId = pickId(payments);
        } catch {
          // Expected fallback when the payments query cannot resolve a receipt.
        }
      }

      if (paymentId) {
        // The button is labeled "Payment receipt" → open the actual receipt.
        // Resolve it from the payment; fall back to the payment detail if the
        // payment has no receipt yet.
        let receiptId: string | undefined;
        try {
          const detail = await queryClient.fetchQuery({
            queryKey: receiptKeys.byPayment(paymentId),
            queryFn: () =>
              apiGet<ReceiptDetail>(operationsClient, endpoints.receipts.forPayment(paymentId!)),
            // No receipt yet is an expected 404 (imported/manual payments) that
            // we recover from — suppress the global error toast for this lookup.
            meta: { suppressToast: true },
          });
          receiptId = detail?.receipt?.id;
        } catch {
          // No receipt for this payment; fall through to the payment detail.
        }

        if (receiptId) {
          void router.navigate({
            to: '/dashboard/receipts/$id',
            params: { id: receiptId },
            search: DEFAULT_RECEIPT_LIST_FILTERS,
          });
          return;
        }

        void router.navigate({
          to: '/dashboard/payments/$id',
          params: { id: paymentId },
          search: DEFAULT_PAYMENT_LIST_FILTERS,
        });
        return;
      }

      // Last fallback (shouldn't happen on a paid invoice): the Payments tab.
      void router.navigate({
        to: '/dashboard/invoices/$id',
        params: { id: invoice.id },
        search: (prev) => ({ ...prev, tab: 'payments' }) as never,
      });
    } finally {
      setResolvingReceipt(false);
    }
  }

  function handleRecordPayment() {
    // `aria-disabled`, not `disabled`, on the header button — see its comment.
    // The refusal has to live here, or the button stays clickable.
    if (recordPaymentBlocked) return;
    if (onPayment) {
      onPayment();
      return;
    }
    setShowPayment(true);
  }

  function handlePaymentLink() {
    if (onPaymentLink) {
      onPaymentLink();
      return;
    }
    setShowPaymentLink(true);
  }

  function handlePreviewPDF() {
    if (onPreviewPDF) {
      onPreviewPDF();
      return;
    }
    setShowPreview(true);
  }

  function issueCreditMemo() {
    if (!contactId) return;
    void router.navigate({
      to: '/dashboard/credit-memos/new',
      search: {
        ...DEFAULT_CREDIT_MEMO_LIST_FILTERS,
        contactId,
        invoiceId: invoice.id,
      },
    });
  }

  async function handleDownloadPDF() {
    if (onDownloadPDF) {
      onDownloadPDF();
      return;
    }
    if (downloading) return;
    setDownloading(true);
    try {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        invoiceEndpoints.detail(invoice.id),
      );
      const full = normalizeInvoice(raw);
      // Resolve the design this invoice renders through so the download matches
      // the office preview and the document the customer receives; null → classic.
      const resolved = await apiGet<{ layout: EstimateDocLayout | null }>(
        operationsClient,
        invoiceEndpoints.documentLayout(invoice.id),
      ).catch(() => ({ layout: null }));
      const { pdf } = await import('@react-pdf/renderer');
      const { InvoiceDocumentPdf } =
        await import('@/components/documents/invoices/InvoiceDocumentPdf');
      const blob = await pdf(
        <InvoiceDocumentPdf
          invoice={full}
          company={companyForPDF}
          timeZone={companyTimeZone}
          wisetack={full.wisetack}
          resolvedDocLayout={resolved.layout}
        />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber ?? 'document'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  }

  function sendReminder() {
    // `aria-disabled` on the header button, as for Record payment.
    if (reminderBlocked) return;
    remindInvoice.mutate({
      id: invoice.id,
      communicationOption: 'email',
    });
  }

  async function confirmVoid() {
    const reason = voidReason.trim();
    if (!reason) {
      toast.error('Void reason is required');
      return;
    }

    setShowVoidConfirm(false);
    try {
      await voidInvoice.mutateAsync({ id: invoice.id, reason });
      setVoidReason('');
    } catch {
      setShowVoidConfirm(true);
      // The shared mutation hook presents the API error to the user.
    }
  }

  async function confirmWriteOff() {
    setShowWriteOffConfirm(false);
    try {
      await writeOffInvoice.mutateAsync(invoice.id);
    } catch {
      setShowWriteOffConfirm(true);
      // The shared mutation hook presents the API error to the user.
    }
  }

  async function confirmDiscard() {
    setShowDiscardConfirm(false);
    try {
      await deleteInvoice.mutateAsync(invoice.id);
      onDiscarded?.();
    } catch {
      setShowDiscardConfirm(true);
      // The shared mutation hook presents the API error to the user.
    }
  }

  function applyPromotion(promotion: Promotion) {
    const replacingExistingDiscount =
      invoice.promotionId !== promotion.id &&
      Boolean(invoice.discountType) &&
      Number(invoice.discountValue ?? 0) > 0;
    if (
      replacingExistingDiscount &&
      !window.confirm('Applying this promotion will replace the current document discount.')
    ) {
      return;
    }
    updateInvoice.mutate(
      {
        id: invoice.id,
        discountType: promotion.discountType === 'percent' ? 'PERCENT' : 'FLAT',
        discountValue: promotion.discountValue,
        discountReason: `Promo: ${promotion.name}`,
        promotionId: promotion.id,
      },
      {
        onSuccess: () => {
          toast.success('Promotion applied');
          setShowPromotionDialog(false);
        },
      },
    );
  }

  function removePromotion() {
    updateInvoice.mutate(
      {
        id: invoice.id,
        discountType: null,
        discountValue: 0,
        discountReason: null,
        promotionId: null,
      },
      {
        onSuccess: () => {
          toast.success('Promotion removed');
          setShowPromotionDialog(false);
        },
      },
    );
  }

  const headerRowOverflow =
    variant === 'header-row' ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon-sm" title="More actions" disabled={isMutating}>
            {isMutating ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <MoreVertical className="size-3.5" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <OverflowMenuContent>
          {canRenderPdf ? (
            <DropdownMenuItem onSelect={handlePreviewPDF}>
              <Eye className="size-4" />
              Preview PDF
            </DropdownMenuItem>
          ) : null}
          {/* Download PDF is already the primary button when !isDraft; in draft it lives here. */}
          {isDraft && canRenderPdf ? (
            <DropdownMenuItem onSelect={() => void handleDownloadPDF()} disabled={downloading}>
              {downloading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Download className="size-4" />
              )}
              Download PDF
            </DropdownMenuItem>
          ) : null}
          {canDiscard ? (
            <>
              {canRenderPdf ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem variant="destructive" onSelect={() => setShowDiscardConfirm(true)}>
                <Ban className="size-4" />
                Discard draft
              </DropdownMenuItem>
            </>
          ) : null}
          {canVoid ? (
            <>
              {canRenderPdf ? <DropdownMenuSeparator /> : null}
              <DropdownMenuItem variant="destructive" onSelect={() => setShowVoidConfirm(true)}>
                <Ban className="size-4" />
                Void
              </DropdownMenuItem>
            </>
          ) : null}
          {canWriteOff ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => setShowWriteOffConfirm(true)}>
                <FileX className="size-4" />
                Write off
              </DropdownMenuItem>
            </>
          ) : null}
        </OverflowMenuContent>
      </DropdownMenu>
    ) : null;

  const headerRowActions =
    variant === 'header-row' ? (
      <div className="flex flex-wrap items-center gap-2">
        {canEditInvoice ? (
          <Button size="sm" variant="outline" onClick={goToEdit}>
            <Edit3 className="size-3.5" />
            Edit invoice
          </Button>
        ) : null}
        {isDraft && !pricingHidden ? (
          <>
            <Button variant="outline" size="sm" onClick={() => setShowSend(true)}>
              <Send className="size-3.5" />
              Send invoice
            </Button>
          </>
        ) : null}
        {canRecordPayment ? (
          // `aria-disabled` rather than `disabled`: a disabled button leaves
          // the tab order, so a keyboard user never reaches it and is told
          // nothing — the same silence as removing it, which is what this
          // change exists to avoid. Kept focusable, described by the reason,
          // and refused in `handleRecordPayment`. The `title` sits on the
          // button itself now; `disabled:pointer-events-none` would have
          // swallowed it, which is why it used to need a wrapper.
          <>
            <Button
              size="sm"
              onClick={handleRecordPayment}
              title={collectionBlockedReason ?? undefined}
              aria-disabled={recordPaymentBlocked || undefined}
              aria-describedby={recordPaymentBlocked ? blockedReasonId : undefined}
              className={recordPaymentBlocked ? 'cursor-not-allowed opacity-50' : undefined}
            >
              <DollarSign className="size-3.5" />
              Record payment
            </Button>
            {recordPaymentBlocked ? (
              <span id={blockedReasonId} className="sr-only">
                {collectionBlockedReason}
              </span>
            ) : null}
          </>
        ) : null}
        {canApplyCredit ? (
          <Button variant="outline" size="sm" onClick={() => setShowApplyCredit(true)}>
            <ArrowRightLeft className="size-3.5" />
            Apply payment credit
          </Button>
        ) : null}
        {canApplyPromotion ? (
          <Button variant="outline" size="sm" onClick={() => setShowPromotionDialog(true)}>
            <Tag className="size-3.5" />
            Apply promotion
          </Button>
        ) : null}
        {canIssueCreditMemo ? (
          <Button variant="outline" size="sm" onClick={issueCreditMemo}>
            <ReceiptText className="size-3.5" />
            Issue credit memo
          </Button>
        ) : null}
        {canSendIssued ? (
          <Button variant="outline" size="sm" onClick={() => setShowSend(true)}>
            <Send className="size-3.5" />
            Send invoice
          </Button>
        ) : null}
        {canSendReminder ? (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={sendReminder}
              disabled={remindInvoice.isPending}
              title={reminderBlocked ? (collectionBlockedReason ?? undefined) : undefined}
              aria-disabled={reminderBlocked || undefined}
              aria-describedby={reminderBlocked ? reminderBlockedReasonId : undefined}
              className={reminderBlocked ? 'cursor-not-allowed opacity-50' : undefined}
            >
              <Send className="size-3.5" />
              Send reminder
            </Button>
            {reminderBlocked ? (
              <span id={reminderBlockedReasonId} className="sr-only">
                {collectionBlockedReason}
              </span>
            ) : null}
          </>
        ) : null}
        {canPaymentLink ? (
          <Button variant="outline" size="sm" onClick={handlePaymentLink}>
            <Link2 className="size-3.5" />
            Payment link
          </Button>
        ) : null}
        {canApplyLateFee ? (
          <Button variant="outline" size="sm" onClick={() => setShowLateFee(true)}>
            <AlertTriangle className="size-3.5" />
            Late fee
          </Button>
        ) : null}
        {canOpenPaymentReceipt ? (
          <Button size="sm" onClick={() => void goToPaymentReceipt()} disabled={resolvingReceipt}>
            {resolvingReceipt ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Receipt className="size-3.5" />
            )}
            Payment receipt
          </Button>
        ) : null}
        {!isDraft && canRenderPdf ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleDownloadPDF()}
            disabled={downloading}
          >
            {downloading ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Download className="size-3.5" />
            )}
            Download PDF
          </Button>
        ) : null}
        {headerRowOverflow}
      </div>
    ) : null;

  return (
    <>
      {variant === 'header-row' ? (
        headerRowActions
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <RowOverflowMenuTrigger title="Invoice actions" disabled={isMutating} />
          </DropdownMenuTrigger>
          <OverflowMenuContent>
            {showViewDetail ? (
              <DropdownMenuItem onSelect={goToDetail}>
                <FileText className="size-4" />
                View detail
              </DropdownMenuItem>
            ) : null}

            {canEditInvoice ? (
              <DropdownMenuItem onSelect={goToEdit}>
                <Edit3 className="size-4" />
                Edit invoice
              </DropdownMenuItem>
            ) : null}
            {isDraft && !pricingHidden ? (
              <>
                {showViewDetail ? <DropdownMenuSeparator /> : null}
                <DropdownMenuItem onSelect={() => setShowSend(true)}>
                  <Send className="size-4" />
                  Send invoice
                </DropdownMenuItem>
              </>
            ) : null}

            {canRecordPayment ||
            canApplyCredit ||
            canApplyPromotion ||
            canIssueCreditMemo ||
            canSendIssued ||
            canSendReminder ||
            canApplyLateFee ||
            canPaymentLink ? (
              <>
                <DropdownMenuSeparator />
                {canRecordPayment ? (
                  <DropdownMenuItem onSelect={handleRecordPayment} disabled={recordPaymentBlocked}>
                    <DollarSign className="size-4" />
                    Record payment
                    {recordPaymentBlocked ? (
                      // Spelled out rather than hovered: a menu item is read,
                      // not pointed at, and a disabled row with no reason is
                      // indistinguishable from a permissions problem.
                      <span className="ml-auto text-xs text-muted-foreground">
                        {collectionBlockedReason}
                      </span>
                    ) : null}
                  </DropdownMenuItem>
                ) : null}
                {canApplyCredit ? (
                  <DropdownMenuItem onSelect={() => setShowApplyCredit(true)}>
                    <ArrowRightLeft className="size-4" />
                    Apply payment credit
                  </DropdownMenuItem>
                ) : null}
                {canApplyPromotion ? (
                  <DropdownMenuItem onSelect={() => setShowPromotionDialog(true)}>
                    <Tag className="size-4" />
                    Apply promotion
                  </DropdownMenuItem>
                ) : null}
                {canIssueCreditMemo ? (
                  <DropdownMenuItem onSelect={issueCreditMemo}>
                    <ReceiptText className="size-4" />
                    Issue credit memo
                  </DropdownMenuItem>
                ) : null}
                {canSendIssued ? (
                  <DropdownMenuItem onSelect={() => setShowSend(true)}>
                    <Send className="size-4" />
                    Send invoice
                  </DropdownMenuItem>
                ) : null}
                {canSendReminder ? (
                  <DropdownMenuItem onSelect={sendReminder} disabled={reminderBlocked}>
                    <Send className="size-4" />
                    Send reminder
                    {reminderBlocked ? (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {collectionBlockedReason}
                      </span>
                    ) : null}
                  </DropdownMenuItem>
                ) : null}
                {canApplyLateFee ? (
                  <DropdownMenuItem onSelect={() => setShowLateFee(true)}>
                    <AlertTriangle className="size-4" />
                    Apply late fee
                  </DropdownMenuItem>
                ) : null}
                {canPaymentLink ? (
                  <DropdownMenuItem onSelect={handlePaymentLink}>
                    <Link2 className="size-4" />
                    Payment link
                  </DropdownMenuItem>
                ) : null}
              </>
            ) : null}

            {canOpenPaymentReceipt ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void goToPaymentReceipt()}>
                  <Receipt className="size-4" />
                  Payment receipt
                </DropdownMenuItem>
              </>
            ) : null}

            {canRenderPdf ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handlePreviewPDF}>
                  <Eye className="size-4" />
                  Preview PDF
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void handleDownloadPDF()} disabled={downloading}>
                  {downloading ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Download className="size-4" />
                  )}
                  Download PDF
                </DropdownMenuItem>
              </>
            ) : null}

            {canDiscard ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => setShowDiscardConfirm(true)}
                >
                  <Ban className="size-4" />
                  Discard draft
                </DropdownMenuItem>
              </>
            ) : null}

            {canVoid ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => setShowVoidConfirm(true)}>
                  <Ban className="size-4" />
                  Void
                </DropdownMenuItem>
              </>
            ) : null}
            {canWriteOff ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => setShowWriteOffConfirm(true)}
                >
                  <FileX className="size-4" />
                  Write off
                </DropdownMenuItem>
              </>
            ) : null}
          </OverflowMenuContent>
        </DropdownMenu>
      )}

      {showSend ? (
        <SendDialog open={showSend} onClose={() => setShowSend(false)} invoice={invoice} />
      ) : null}

      {canCollectPayment && showPayment ? (
        <RecordPaymentDialog
          open={showPayment}
          onClose={() => setShowPayment(false)}
          invoiceId={invoice.id}
          balanceDue={shownBalanceDue}
          balanceDueKnown={balanceDueKnown}
          contactId={invoice.contactId}
          contactName={
            invoice.contact
              ? getContactDisplayName({
                  firstName: invoice.contact.firstName,
                  lastName: invoice.contact.lastName,
                  companyName: invoice.contact.company,
                })
              : undefined
          }
          contactEmail={invoice.contact?.primaryEmail ?? null}
          billToContactId={invoice.billToContact?.contactId ?? null}
          billToContactName={
            invoice.billToContact?.contactName ?? invoice.billToContact?.name ?? null
          }
          canEditPayments={canEditPayments}
          clearingReason={collectionBlockedReason}
        />
      ) : null}

      {contactId && showApplyCredit ? (
        <ApplyCreditModal
          open={showApplyCredit}
          onOpenChange={setShowApplyCredit}
          contactId={contactId}
          invoiceId={invoice.id}
          invoiceNumber={invoice.invoiceNumber}
          balanceDue={shownBalanceDue}
          creditAvailable={creditAvailable}
        />
      ) : null}

      <PaymentLinkDialog
        open={showPaymentLink}
        onClose={() => setShowPaymentLink(false)}
        invoiceId={invoice.id}
        existingLink={invoice.paymentLink}
      />

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent
          className="w-full max-w-4xl p-0"
          showCloseButton={false}
          onOverlayClick={() => setShowPreview(false)}
        >
          <DialogTitle className="sr-only">Invoice Preview</DialogTitle>
          <DialogDescription className="sr-only">
            Preview the generated invoice PDF before download or delivery.
          </DialogDescription>
          {fullInvoiceQuery.isLoading ? (
            <div className="flex min-h-[60vh] items-center justify-center">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="flex min-h-[60vh] items-center justify-center">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              }
            >
              {showPreview && fullInvoiceQuery.data ? (
                <InvoicePDFPreview
                  invoice={fullInvoiceQuery.data}
                  company={companyForPDF}
                  timeZone={companyTimeZone}
                />
              ) : null}
            </Suspense>
          )}
        </DialogContent>
      </Dialog>

      <LateFeeDialog
        open={showLateFee}
        onClose={() => setShowLateFee(false)}
        invoice={invoice}
        balanceDue={balanceDue}
        applyLateFee={applyLateFee}
      />

      <PromotionDialog
        open={showPromotionDialog}
        onClose={() => setShowPromotionDialog(false)}
        invoice={invoice}
        promotions={promotionsQuery.data ?? []}
        isLoading={promotionsQuery.isLoading}
        isPending={updateInvoice.isPending}
        onApply={applyPromotion}
        onRemove={removePromotion}
      />

      <AlertDialog open={showVoidConfirm} onOpenChange={setShowVoidConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              Invoice {invoiceReference(invoice)} will be marked as void and can no longer be paid.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Reason for voiding this invoice..."
            value={voidReason}
            onChange={(event) => setVoidReason(event.target.value)}
            rows={3}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Keep invoice</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(event) => {
                event.preventDefault();
                void confirmVoid();
              }}
              disabled={!voidReason.trim() || voidInvoice.isPending}
            >
              {voidInvoice.isPending ? (
                <Loader2 className="mr-1 size-3.5 animate-spin" />
              ) : (
                <Ban className="mr-1 size-3.5" />
              )}
              Void
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard draft invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This draft invoice will be permanently removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep draft</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(event) => {
                event.preventDefault();
                confirmDiscard();
              }}
            >
              {deleteInvoice.isPending ? (
                <Loader2 className="mr-1 size-3.5 animate-spin" />
              ) : (
                <Ban className="mr-1 size-3.5" />
              )}
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showWriteOffConfirm} onOpenChange={setShowWriteOffConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Write off invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              The remaining balance on invoice {invoiceReference(invoice)} will be written off. This
              is a terminal action.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep invoice</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(event) => {
                event.preventDefault();
                void confirmWriteOff();
              }}
              disabled={writeOffInvoice.isPending}
            >
              {writeOffInvoice.isPending ? (
                <Loader2 className="mr-1 size-3.5 animate-spin" />
              ) : (
                <FileX className="mr-1 size-3.5" />
              )}
              Write off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function PromotionDialog({
  open,
  onClose,
  invoice,
  promotions,
  isLoading,
  isPending,
  onApply,
  onRemove,
}: {
  open: boolean;
  onClose: () => void;
  invoice: Invoice;
  promotions: Promotion[];
  isLoading: boolean;
  isPending: boolean;
  onApply: (promotion: Promotion) => void;
  onRemove: () => void;
}) {
  const hasExistingDiscount = Number(invoice.discountValue ?? invoice.discountTotal ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="sm:max-w-lg" onOverlayClick={onClose}>
        <DialogHeader>
          <DialogTitle>Apply promotion</DialogTitle>
          <DialogDescription>
            Choose an active promotion for invoice {invoiceReference(invoice)}.
            {hasExistingDiscount ? ' Applying one replaces the current invoice discount.' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
          {isLoading ? (
            <div className="flex min-h-24 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 size-4 animate-spin" />
              Loading promotions
            </div>
          ) : promotions.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No active promotions
            </div>
          ) : (
            promotions.map((promotion) => (
              <button
                key={promotion.id}
                type="button"
                className="flex w-full items-center justify-between gap-3 rounded-md border bg-background px-3 py-2 text-left text-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => onApply(promotion)}
                disabled={isPending}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{promotion.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {promotion.promoCode ? `${promotion.promoCode} · ` : ''}
                    Expires {formatPromotionDate(promotion.endDate)}
                  </span>
                </span>
                <span className="shrink-0 font-medium">{formatPromotionDiscount(promotion)}</span>
              </button>
            ))
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {invoice.promotionId ? (
            <Button variant="outline" onClick={onRemove} disabled={isPending}>
              Remove promotion
            </Button>
          ) : (
            <span />
          )}
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LateFeeDialog({
  open,
  onClose,
  invoice,
  balanceDue,
  applyLateFee,
}: {
  open: boolean;
  onClose: () => void;
  invoice: Invoice;
  balanceDue: number;
  applyLateFee: ReturnType<typeof useApplyLateFee>;
}) {
  const [lateFeeAmount, setLateFeeAmount] = useState('25');
  const [lateFeeDescription, setLateFeeDescription] = useState('Late payment fee');

  function handleApply() {
    const amount = Number(lateFeeAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Late fee must be greater than zero');
      return;
    }

    applyLateFee.mutate(
      {
        id: invoice.id,
        amount,
        description: lateFeeDescription.trim() || undefined,
      },
      {
        onSuccess: onClose,
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="sm:max-w-md" onOverlayClick={onClose}>
        <DialogHeader>
          <DialogTitle>Apply late fee</DialogTitle>
          <DialogDescription>
            Add a non-taxable fee to invoice {invoiceReference(invoice)}. The current balance is{' '}
            {formatCurrency(balanceDue)}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Amount</p>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={lateFeeAmount}
              onChange={(event) => setLateFeeAmount(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Description</p>
            <Input
              value={lateFeeDescription}
              onChange={(event) => setLateFeeDescription(event.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={applyLateFee.isPending}>
            {applyLateFee.isPending ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
            Apply fee
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatPromotionDiscount(promotion: Promotion) {
  return promotion.discountType === 'percent'
    ? `${promotion.discountValue}% off`
    : `${formatCurrency(promotion.discountValue)} off`;
}

function formatPromotionDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}
